'use client'

import { subscribeNewsLetter } from '@/actions/authentication';
import Button from '@/components/ui/Button';
import PopupWrapper from '@/components/ui/PopupWrapper';
import { useUserStore } from '@/core/stores/user';
import useSizeController from '@/hooks/useSizeController';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import { useState } from 'react';
import dynamic from 'next/dynamic';

// Lazy load Confetti and Congrate components
const Confetti = dynamic(() => import('react-confetti'), { ssr: false });
const Congrate = dynamic(() => import('../../payment/(response)/successful/congrate'), { ssr: false });

export default function HomePopup() {
    const wpAsset = process.env.NEXT_PUBLIC_WP_ASSETS_BASE_URL
    const [email, setEmail] = useState('');
    const [success, setSuccess] = useState(false);
    const [close, setClose] = useState(false);
    const [error, setError] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    const { innerHeight, innerWidth } = useSizeController();
    const { _id } = useUserStore()

    async function subscribe() {
        setIsLoading(true)
        const res = await subscribeNewsLetter(email);
        const { result, response } = JSON.parse(res);
        if (response.ok) {
            setSuccess(true);
            setTimeout(() => {
                setClose(true);
            }, 6000);
        } else if (response.status === 400) {
            setError('Seems like you are already subscribed! ')
        }
        setIsLoading(false)
    }

    return !_id && (
        <PopupWrapper close={close} identifier='newsletter'>
            <div className='md:grid py-4 md:py-0 min-w-80 md:grid-cols-[280px,_480px] gap-2 font-semibold text-lg'>
                <div className='md:grid py-4 md:py-0 min-w-80 md:grid-cols-[280px,_480px] gap-2 font-semibold text-lg'>
                    <div className='md:bg-indigo-500 flex items-center justify-center relative'>
                        <div className='absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 w-52 bg-indigo-500 rounded-full aspect-square z-0'></div>
                        <Image
                            width={400}
                            height={400}
                            className='relative z-10 w-2/3 md:w-full h-full'
                            alt='Newsletter Image'
                            src={`${process.env.NEXT_PUBLIC_STORAGE_URL}assets/59db0e5b-a4bd-413f-9915-6d3748ed4c95.png`}
                            loading="lazy" // lazy load the image
                        />
                    </div>
                    <AnimatePresence mode='wait'>
                        {success ? (
                            <motion.div
                                key='success'
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{ duration: 0.5 }}
                                className='flex items-center justify-center gap-2 flex-col h-full w-full'>
                                <Confetti width={innerWidth} height={innerHeight || undefined} />
                                <Congrate />
                                <p className='mt-4 text-sm text-neutral-700'>You won&apos;t regret it!</p>
                            </motion.div>
                        ) : (<motion.div
                            key='content'
                            initial={{ opacity: 0, x: -50 }}
                            animate={{ opacity: 1, x: 0 }}
                            exit={{ opacity: 0, x: 50 }}
                            transition={{ duration: 0.5 }}
                            className='flex flex-col px-4 md:px-6 mt-6 md:mt-0 items-start justify-center h-full'>
                            <p className='text-neutral-900'>Travel Smarter, Travel Greener!</p>
                            <p className='text-neutral-600 mt-1 font-normal text-sm'>
                                Submit your email and get informed on exclusive hotel deals and save big on your next adventure.
                            </p>
                            <div className='mt-8 flex md:flex-row flex-col items-start gap-2 w-full'>
                                <div className='w-full flex flex-col'>
                                    <input
                                        aria-label="Email address"
                                        value={email}
                                        onChange={(e) => {
                                            setError('')
                                            setEmail(e.target.value)
                                        }}
                                        className='outline-none ring-0 text-neutral-500 font-normal h-10 w-full rounded-lg 
                                    grow focus:border-indigo-500 border-2 border-neutral-200'
                                    />
                                    {error && <p className='text-rose-500 font-normal text-xs mt-1'>{error}</p>}
                                </div>
                                <Button isLoading={isLoading} onClick={subscribe} className='w-full md:w-auto min-w-max' color='indigo'>
                                    Get Started
                                </Button>
                            </div>
                        </motion.div>)}
                    </AnimatePresence>
                </div>
            </div>
        </PopupWrapper>
    );
}
